import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ui, cn } from '../lib/ui';
import { pageHref, resolvePageId } from '../lib/sites';
import * as sitesApi from '../api/sites';
import * as publicApi from '../api/public';
import SiteTemplate from '../components/sites/SiteTemplate';

/**
 * Public template site, /s/:slug and /s/:slug/:page (docs/Decisions.md D43).
 * No sign-in. Only a published site is served; anything else is "not found".
 *
 * On an organization's own website host (D60 item 4) it is mounted at the
 * root instead: `slug` comes from the host, `basePath` is '', and app pages
 * link to the portal at `appOrigin`.
 */
export default function PublicSite({ slug: hostSlug, basePath, appOrigin = '' }) {
  const params = useParams();
  const slug = hostSlug || params.slug;
  const page = params.page;
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [events, setEvents] = useState({ state: 'idle', items: [] });

  useEffect(() => {
    let live = true;
    setState({ loading: true, error: null, data: null });
    sitesApi
      .getPublicSite(slug)
      .then(data => live && setState({ loading: false, error: null, data }))
      .catch(err => live && setState({ loading: false, error: err, data: null }));
    return () => {
      live = false;
    };
  }, [slug]);

  const data = state.data;
  const pageId = data ? resolvePageId(data.site.pages, page) : null;
  const wantsEvents =
    Boolean(data) &&
    (pageId === 'events' || (data.site.template === 'community' && pageId === 'home'));

  useEffect(() => {
    if (!wantsEvents) return undefined;
    let live = true;
    setEvents({ state: 'loading', items: [] });
    publicApi
      .listOrgEvents(slug, { limit: 20 })
      .then(res => live && setEvents({ state: 'ready', items: res.events || [] }))
      .catch(() => live && setEvents({ state: 'error', items: [] }));
    return () => {
      live = false;
    };
  }, [slug, wantsEvents]);

  useEffect(() => {
    if (data && pageId) {
      const pageTitle = data.site.pages[pageId]?.title;
      document.title =
        pageId === 'home'
          ? data.site.siteTitle || data.org.name
          : `${pageTitle} - ${data.site.siteTitle || data.org.name}`;
    }
  }, [data, pageId]);

  if (state.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-z-canvas" data-zone="member">
        <p className={ui.muted} role="status">
          Loading the site.
        </p>
      </div>
    );
  }

  if (!data || !pageId) {
    const notFound = data ? true : state.error?.statusCode === 404;
    return (
      <div className="min-h-screen bg-z-canvas" data-zone="member">
        <main className={cn(ui.container, ui.page)}>
          <h1 className={ui.h1}>
            {notFound ? 'This page is not available' : 'The site could not be loaded'}
          </h1>
          <p className={ui.muted}>
            {notFound
              ? 'The address may be mistyped, or the organization has not published this page.'
              : 'Try again in a moment.'}
          </p>
          {data ? (
            <Link className={cn(ui.btnSecondary, 'mt-4')} to={pageHref(slug, 'home', basePath)}>
              Go to the home page
            </Link>
          ) : null}
        </main>
      </div>
    );
  }

  return (
    <SiteTemplate
      site={data.site}
      orgName={data.org.name}
      slug={data.org.slug}
      logoUrl={data.logoUrl}
      hostedUrl={data.hostedUrl}
      pageId={pageId}
      events={events.items}
      eventsState={events.state}
      basePath={basePath}
      appOrigin={appOrigin}
    />
  );
}
